import React, { useMemo, useState } from 'react';
import { Box, Paper, IconButton, Typography, Popover, Chip, Divider, Tooltip } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { isViewOnly } from '../utils/permissions';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CategoryIcon from '@mui/icons-material/Category';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import StraightenIcon from '@mui/icons-material/Straighten';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import * as geojson from 'geojson';
import { AssetRiskReportModal } from './assetReport';
import AssetRadiantExposure from './AssetRadiantExposure';
import { useUnitSystem } from '../hooks/useUnitSystem';
import { formatLength } from '../utils/units';
import {
  CONSTRUCTION_CATEGORIES,
  ConstructionCategory,
  asIdArray,
  isBuildingAssetType,
  useConstructionTypes,
  useConstructionSettings,
} from '../api/constructionTypes';
import { useProject } from '../api/project';

interface AssetPopoverProps {
  asset: geojson.Feature | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  onEdit: (asset: geojson.Feature) => void;
  onDelete: (assetId: string | number) => void;
}

const ACCENT = '#00d4ad';

// Helper function to check if a value exists and is not undefined
const hasValue = (value: any): boolean => {
  return value !== undefined && value !== null && value !== '';
};

const isLineLikeAssetType = (assetType: unknown): boolean => {
  if (typeof assetType !== 'string') return false;
  return /line|road|highway/i.test(assetType);
};

interface ResolvedSetting {
  id: number;
  name: string;
  category: ConstructionCategory;
  isExtra: boolean;
}

const CATEGORY_LABELS: Record<string, string> = CONSTRUCTION_CATEGORIES.reduce(
  (m, c) => {
    m[c.value] = c.label;
    return m;
  },
  {} as Record<string, string>
);

/**
 * Construction section: shows the asset's construction type plus every
 * assigned setting (type-derived + per-asset overrides) as chips grouped
 * by category.  Setting categories are resolved from the cached construction
 * type / settings queries since the tile/asset properties only carry names.
 */
const ConstructionSection: React.FC<{ asset: geojson.Feature }> = ({ asset }) => {
  const typesQ = useConstructionTypes();
  const settingsQ = useConstructionSettings();
  const { project } = useProject();

  // Construction only applies to buildings; roads/power lines never inherit it.
  const isBuilding = isBuildingAssetType(
    asset.properties?.asset_type ?? asset.properties?.assetType
  );
  const ownTypeId: number | null = asset.properties?.construction_type_id ?? null;
  const defaultTypeId: number | null = project?.data?.default_construction_type ?? null;
  // Buildings without their own type inherit the project default.
  const inheritingDefault = isBuilding && ownTypeId == null && defaultTypeId != null;
  const effectiveTypeId = ownTypeId ?? (isBuilding ? defaultTypeId : null);
  const extraIds: number[] = asIdArray(asset.properties?.extra_construction_setting_ids);
  const extraKey = extraIds.join(",");

  const { resolvedByCategory, orderedCategories, effectiveTypeName } = useMemo(() => {
    const types = typesQ.data?.data ?? [];
    const allSettings = settingsQ.data?.data ?? [];
    const settingsById = new Map(allSettings.map((s) => [s.id, s]));

    const collected: ResolvedSetting[] = [];

    // Type-derived settings (carry their own category in the embedded payload).
    const type =
      effectiveTypeId != null ? types.find((t) => t.id === effectiveTypeId) : undefined;
    if (type) {
      for (const s of type.settings) {
        collected.push({ id: s.id, name: s.name, category: s.category, isExtra: false });
      }
    }

    // Per-asset extra overrides.
    for (const id of extraIds) {
      const s = settingsById.get(id);
      if (s) {
        collected.push({ id: s.id, name: s.name, category: s.category, isExtra: true });
      }
    }

    // Group by category, preserving the canonical category order.
    const byCat: Record<string, ResolvedSetting[]> = {};
    for (const item of collected) {
      (byCat[item.category] ??= []).push(item);
    }
    const ordered = CONSTRUCTION_CATEGORIES.map((c) => c.value).filter(
      (c) => byCat[c]?.length
    );
    // Prefer the tile-provided name for the asset's own type; otherwise the
    // resolved (possibly project-default) type's name.
    const name =
      asset.properties?.construction_type_name ?? type?.name ?? null;
    return {
      resolvedByCategory: byCat,
      orderedCategories: ordered,
      effectiveTypeName: name,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typesQ.data, settingsQ.data, effectiveTypeId, extraKey]);

  // Nothing to show — no effective type and no extras.
  if (!hasValue(effectiveTypeName) && orderedCategories.length === 0) {
    return null;
  }

  const hasExtras = Object.values(resolvedByCategory).some((arr) =>
    arr.some((s) => s.isExtra)
  );

  return (
    <Box sx={{ mt: 1.5 }}>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 1.25 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', mb: orderedCategories.length ? 1.25 : 0 }}>
        <HomeWorkIcon sx={{ mr: 1, fontSize: 18, color: ACCENT }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {effectiveTypeName ?? 'Custom construction'}
        </Typography>
        {inheritingDefault && (
          <Typography variant="caption" sx={{ ml: 0.75, color: 'rgba(255,255,255,0.5)' }}>
            (project default)
          </Typography>
        )}
      </Box>

      {orderedCategories.map((cat) => (
        <Box key={cat} sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontSize: '0.62rem',
              fontWeight: 700,
              letterSpacing: 0.7,
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.45)',
              mb: 0.5,
            }}
          >
            {CATEGORY_LABELS[cat] ?? cat}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {resolvedByCategory[cat].map((s) => (
              <Chip
                key={`${s.id}-${s.isExtra ? 'x' : 't'}`}
                label={s.name}
                size="small"
                sx={
                  s.isExtra
                    ? {
                        height: 22,
                        bgcolor: 'rgba(0,212,173,0.16)',
                        color: ACCENT,
                        border: `1px solid ${ACCENT}`,
                        fontWeight: 500,
                        '& .MuiChip-label': { px: 1 },
                      }
                    : {
                        height: 22,
                        bgcolor: 'rgba(255,255,255,0.08)',
                        color: 'rgba(255,255,255,0.85)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        '& .MuiChip-label': { px: 1 },
                      }
                }
              />
            ))}
          </Box>
        </Box>
      ))}

      {hasExtras && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '2px',
              bgcolor: 'rgba(0,212,173,0.16)',
              border: `1px solid ${ACCENT}`,
            }}
          />
          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>
            asset-specific override
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const AssetPopover: React.FC<AssetPopoverProps> = ({
  asset,
  anchorPosition,
  onClose,
  onEdit,
  onDelete,
}) => {
  const open = Boolean(asset && anchorPosition);
  const [riskReportOpen, setRiskReportOpen] = useState(false);
  const unitSystem = useUnitSystem();
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);

  const handleRiskReportOpen = () => {
    setRiskReportOpen(true);
  };

  const handleRiskReportClose = () => {
    setRiskReportOpen(false);
  };

  if (!asset) return null;

  return (
    <>
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Paper sx={{ p: 2, width: 320, maxWidth: 320, bgcolor: '#212b37', color: 'white' }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
          {asset.properties?.name || 'Unnamed Asset'}
        </Typography>

        {hasValue(asset.properties?.value) && (
          <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <AttachMoneyIcon sx={{ mr: 1, fontSize: 18 }} />
            Value: {asset.properties?.value}
          </Typography>
        )}

        {hasValue(asset.properties?.asset_type ?? asset.properties?.assetType) && (
          <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <CategoryIcon sx={{ mr: 1, fontSize: 18 }} />
            Type: {asset.properties?.asset_type ?? asset.properties?.assetType}
          </Typography>
        )}
        {hasValue(asset.properties?.description) && (
          <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 1, fontSize: 18 }} />
            Description: {asset.properties?.description}
          </Typography>
        )}

        {(hasValue(asset.properties?.width) ||
          isLineLikeAssetType(asset.properties?.asset_type ?? asset.properties?.assetType)) && (
          <Typography variant="body2" sx={{ mb: 0.5, display: 'flex', alignItems: 'center' }}>
            <StraightenIcon sx={{ mr: 1, fontSize: 18 }} />
            Width: {hasValue(asset.properties?.width) && Number.isFinite(Number(asset.properties?.width))
              ? formatLength(Number(asset.properties?.width), unitSystem)
              : '—'}
          </Typography>
        )}

        <ConstructionSection asset={asset} />

        {/* What the selected simulation's fire did to this building. */}
        <AssetRadiantExposure asset={asset} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Tooltip title="Generate Risk Report">
            <IconButton
              size="small"
              onClick={handleRiskReportOpen}
              sx={{ color: ACCENT, mr: 1 }}
            >
              <AssessmentIcon />
            </IconButton>
          </Tooltip>

          {!viewOnly && (
            <Tooltip title="Edit asset">
              <IconButton
                size="small"
                onClick={() => onEdit(asset)}
                sx={{ color: ACCENT, mr: 1 }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}

          {!viewOnly && (
            <Tooltip title="Delete asset">
              <IconButton
                size="small"
                onClick={() => asset.id && onDelete(asset.id)}
                sx={{ color: '#ff5252' }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>
    </Popover>

    <AssetRiskReportModal
      asset={asset}
      open={riskReportOpen}
      onClose={handleRiskReportClose}
    />
  </>
  );
};

export default AssetPopover;
